import type { PublicClient } from "viem"
import { useCallback, useMemo, useState } from "react"
import BigNumber from "bignumber.js"
import { observer } from "mobx-react-lite"
import { formatUnits } from "viem"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm/cl-farm-store"
import { AlmProviderImageLink } from "@/domains/cl-farms-page/components/alm-provider-image-link"
import { ClFarmsSubHeader } from "@/domains/cl-farms-page/components/cl-farms-sub-header"
import { DollarIcon, WalletIcon } from "@/domains/icons"
import { mintGammaClFarmLiquidityInteraction } from "@/domains/interactions"
import { dollars } from "@/domains/library"
import { TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputSymbol } from "@/domains/tokens/components/token-input-symbol"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons, TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useDebouncedCallback } from "@/hooks/use-debounced-callback"
import useEventEmitter from "@/hooks/useEventEmitter"

import { INSUFFICIENT_BALANCE_ERROR } from "../../../../features/liquidity/constants"

const getTotalAmountsAbi = [
  {
    inputs: [],
    name: "getTotalAmounts",
    outputs: [
      { internalType: "uint256", name: "total0", type: "uint256" },
      { internalType: "uint256", name: "total1", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const

export const MintGammaClFarmPair = observer(({ clFarm }: { clFarm: ClFarmStore | null | undefined }) => {
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()

  const scaleApp = useScaleApplication()

  const publicClient = scaleApp.getPublicClient()

  const [amountToken0, setAmountToken0] = useState<string>("0")
  const [amountToken1, setAmountToken1] = useState<string>("0")

  // auto-balance token inputs on change
  const onToken0ValueChanged = useCallback(
    async (newValue: string) => {
      if (!newValue) {
        setAmountToken0("0")
        setAmountToken1("0")
        return
      }

      if (!clFarm) {
        return
      }

      const vaultAddress = clFarm.vaultAddress.toViemAddress()

      // now we need to work out the token 1 amount
      const result = (await publicClient.readContract({
        address: vaultAddress,
        abi: getTotalAmountsAbi,
        functionName: "getTotalAmounts",
      })) as [bigint, bigint]

      // what % of token0 Amount is `value`?
      const newValueBN = BigNumber(Number(newValue) > 0 ? newValue : "0")
      const token0VaultValue = formatUnits(result[0], clFarm.token0.decimals)
      const token1VaultValue = formatUnits(result[1], clFarm.token1.decimals)

      const token0VaultValueBN = BigNumber(token0VaultValue)
      const token1VaultValueBN = BigNumber(token1VaultValue)
      const vaultPercentageBN = token0VaultValueBN.gt(0) ? newValueBN.div(token0VaultValueBN) : BigNumber(0)
      const token1ValueBN = token1VaultValueBN.times(vaultPercentageBN)

      // dont change the value thats changed or the UI will glitch (cursor position gets lost)
      // setAmountToken0(newValueBN.toFixed(clFarm.token0.decimals))
      setAmountToken1(token1ValueBN.toFixed(clFarm.token1.decimals))
    },
    [clFarm?.token0?.decimals, clFarm?.token1?.decimals, publicClient],
  )

  const onToken0ValueChangedDebounced = useDebouncedCallback(onToken0ValueChanged, 1000)

  const handleToken0Change = useCallback(
    (value: string) => {
      // Update UI immediately
      setAmountToken0(value)
      // Debounce the expensive calculation
      onToken0ValueChangedDebounced(value)
    },
    [onToken0ValueChangedDebounced],
  )

  const handleToken0ChangeButtons = useCallback(
    (value: string) => {
      // Update UI immediately
      setAmountToken0(value)
      // Update the the expensive calculation immediately too
      // Buttons are clicked once, not typed
      onToken0ValueChanged(value)
    },
    [onToken0ValueChangedDebounced],
  )

  const onToken1ValueChanged = useCallback(
    async (newValue: string) => {
      if (!newValue) {
        setAmountToken0("0")
        setAmountToken1("0")
        return
      }

      if (!clFarm) {
        return
      }

      const vaultAddress = clFarm.vaultAddress.toViemAddress()

      // now we need to work out the token 0 amount
      const result = (await publicClient.readContract({
        address: vaultAddress,
        abi: getTotalAmountsAbi,
        functionName: "getTotalAmounts",
      })) as [bigint, bigint]

      // what % of token0 Amount is `value`?
      const newValueBN = BigNumber(newValue ?? "0")
      const token0VaultValue = formatUnits(result[0], clFarm.token0.decimals)
      const token1VaultValue = formatUnits(result[1], clFarm.token1.decimals)

      const token0VaultValueBN = BigNumber(token0VaultValue)
      const token1VaultValueBN = BigNumber(token1VaultValue)
      const vaultPercentageBN = newValueBN.div(token1VaultValueBN)
      const token0ValueBN = token0VaultValueBN.times(vaultPercentageBN)

      setAmountToken0(token0ValueBN.toFixed(clFarm.token0.decimals))
      // don't change the value thats changed or the UI will glitch (cursor position gets lost)
      // setAmountToken1(newValueBN.toFixed(clFarm.token1.decimals))
    },
    [clFarm?.token0?.decimals, clFarm?.token1?.decimals, publicClient],
  )

  const onToken1ValueChangedDebounced = useDebouncedCallback(onToken1ValueChanged, 1000)

  const handleToken1Change = useCallback(
    (value: string) => {
      // Update UI immediately
      setAmountToken1(value)
      // Debounce the expensive calculation
      onToken1ValueChangedDebounced(value)
    },
    [onToken1ValueChangedDebounced],
  )

  const handleToken1ChangeButtons = useCallback(
    (value: string) => {
      // Update UI immediately
      setAmountToken1(value)
      // Update the the expensive calculation immediately too
      // Buttons are clicked once, not typed
      onToken1ValueChanged(value)
    },
    [onToken1ValueChangedDebounced],
  )

  if (!clFarm) {
    return null
  }

  const token0AmountBI = clFarm.token0.parseUnits(amountToken0)
  const token0ValueUsd = clFarm.token0.calculateValueUSD(Number(amountToken0))
  const token1AmountBI = clFarm.token1.parseUnits(amountToken1)
  const token1ValueUsd = clFarm.token1.calculateValueUSD(Number(amountToken1))

  // has sufficient balance
  const hasSufficientBalanceToken0 = token0AmountBI <= clFarm.token0.balance
  const hasSufficientBalanceToken1 = token1AmountBI <= clFarm.token1.balance

  const canInteract =
    token0AmountBI > 0n && token1AmountBI > 0n && hasSufficientBalanceToken0 && hasSufficientBalanceToken1

  const mintAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    const chainId = chainConfig.chainId

    const publicClient = scaleApp.getPublicClient()

    await mintGammaClFarmLiquidityInteraction({
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress?.toViemAddress(),
      chainId,
      config,
      clFarm,
      publicClient,
      emitter,
      token0AmountBI,
      token1AmountBI,
      approvalMode: scaleApp?.Settings.approvalMode,
      onSuccess: () => {
        setAmountToken0("0")
        setAmountToken1("0")
        // we update our CL Farm User Info
        scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

        // the token should update it's balance
        scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
      },
    })
  }

  const buttonsToken0: ITokenPercentageButtons = {
    symbol: clFarm.token0.symbol ?? "unknown",
    imageURL: clFarm.token0.imageURL ?? "",
    decimals: clFarm.token0.decimals ?? 18,
    calculateValue: (percentage: number): string => {
      return clFarm.token0.calculateValuePercentage(clFarm.token0.balance, percentage) ?? "0"
    },
  }
  const buttonsToken1: ITokenPercentageButtons = {
    symbol: clFarm.token1.symbol ?? "unknown",
    imageURL: clFarm.token1.imageURL ?? "",
    decimals: clFarm.token1.decimals ?? 18,
    calculateValue: (percentage: number): string => {
      return clFarm.token1.calculateValuePercentage(clFarm.token1.balance, percentage) ?? "0"
    },
  }

  return (
    <Card.Container className="flex w-full min-w-[320px] flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <ShowWhenTrue when={!!clFarm}>
        <ClFarmsSubHeader displayName={clFarm.displayName} tags={clFarm.tags} description={clFarm.description} />

        <InnerCard hasWarning={!hasSufficientBalanceToken0}>
          <div className="flex flex-row items-center justify-between">
            <TokenInputValue
              value={amountToken0}
              token={clFarm.token0}
              onValueChange={(v) => handleToken0Change(v)}
              className="h-fit min-w-[180px] flex-grow p-0"
              placeholder="0"
            />
            <TokenInputSymbol token={clFarm.token0} className="mr-2" />
          </div>

          {/* balances */}
          <div className="flex w-full flex-row items-center justify-between text-xs">
            <ShowWhenTrue when={hasSufficientBalanceToken0}>
              <div className="flex items-center gap-1 text-sm text-t200">
                <DollarIcon size={14} className="text-t200" />
                <p>~{dollars(token0ValueUsd)}</p>
              </div>
            </ShowWhenTrue>
            <ShowWhenTrue when={!hasSufficientBalanceToken0}>
              <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
            </ShowWhenTrue>
            <Tooltip key={"balance"}>
              <TooltipTrigger asChild>
                <TokenInputBalance token={clFarm.token0} label={<WalletIcon size={14} className="text-t200" />} />
              </TooltipTrigger>
              <TooltipContent>
                <div>{clFarm.token0.balance.toString()}</div>
              </TooltipContent>
            </Tooltip>
          </div>

          <TokenPercentageButtons token={buttonsToken0} onClick={handleToken0ChangeButtons} />
        </InnerCard>
        <InnerCard hasWarning={!hasSufficientBalanceToken1}>
          <div className="flex flex-row items-center justify-between">
            <TokenInputValue
              value={amountToken1}
              token={clFarm.token1}
              onValueChange={(v) => handleToken1Change(v)}
              className="h-fit min-w-[180px] flex-grow p-0"
              placeholder="0"
            />
            <TokenInputSymbol token={clFarm.token1} className="mr-2" />
          </div>

          {/* balances */}
          <div className="flex w-full flex-row items-center justify-between text-xs">
            <ShowWhenTrue when={hasSufficientBalanceToken1}>
              <div className="flex items-center gap-1 text-sm text-t200">
                <DollarIcon size={14} className="text-t200" />
                <p>~{dollars(token1ValueUsd)}</p>
              </div>
            </ShowWhenTrue>
            <ShowWhenTrue when={!hasSufficientBalanceToken1}>
              <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
            </ShowWhenTrue>
            <Tooltip key={"balance"}>
              <TooltipTrigger asChild>
                <TokenInputBalance token={clFarm.token1} label={<WalletIcon size={14} className="text-t200" />} />
              </TooltipTrigger>
              <TooltipContent>
                <div>{clFarm.token1.balance.toString()}</div>
              </TooltipContent>
            </Tooltip>
          </div>

          <TokenPercentageButtons token={buttonsToken1} onClick={handleToken1ChangeButtons} />
        </InnerCard>

        <div className="my-2 flex w-full justify-between">
          <AlmProviderImageLink clFarm={clFarm} />
          <Button disabled={!canInteract} variant={"primary"} onClick={mintAction} size="fit">
            Mint
          </Button>
        </div>
      </ShowWhenTrue>
    </Card.Container>
  )
})
